import type { EventBus } from '../EventBus';
import type { PlatformAdapter } from '../adapters/PlatformAdapter';
import type { LocalStorageService } from './browser/LocalStorageService';
import type { TrackingDebugService } from './TrackingDebugService';
import { StorageKey } from '../config/storageKeys';
import { TrackingBusEvent } from '../constants/trackingBusEvent';
import { TrackingTag } from '../constants/trackingTag';

interface CustomerResolvedPayload {
  customerId: string | null;
}

/**
 * Resolves the logged-in customer id from the **platform adapter** (Shopware: JSON route) and mirrors it in
 * localStorage. Uses **`emitCrossTab`** so every open tab updates without a second HTTP round-trip when the
 * first tab finishes resolution.
 */
export class CustomerService {
  private inMemoryCustomerId: string | null = null;

  constructor(
    private readonly storage: LocalStorageService,
    private readonly adapter: PlatformAdapter,
    private readonly bus: EventBus,
    private readonly debug?: TrackingDebugService,
  ) {}

  init(): void {
    const cached = this.storage.get(StorageKey.CustomerId);
    this.inMemoryCustomerId = cached;

    this.bus.subscribe<CustomerResolvedPayload>(
      TrackingBusEvent.CustomerResolved,
      ({ customerId }) => {
        this.inMemoryCustomerId = customerId;
        if (customerId == null) {
          this.storage.remove(StorageKey.CustomerId);
        } else {
          this.storage.set(StorageKey.CustomerId, customerId);
        }
      },
      TrackingTag.CustomerService,
    );

    this.bus.subscribe(
      TrackingBusEvent.ConsentWithdrawn,
      () => {
        this.storage.remove(StorageKey.CustomerId);
        this.inMemoryCustomerId = null;
      },
      TrackingTag.CustomerService,
    );

    // Async resolve in background (does not block startup).
    void this.resolveCustomerId();
  }

  getCustomerId(): string | null {
    return this.inMemoryCustomerId;
  }

  private async resolveCustomerId(): Promise<void> {
    try {
      const customerId = await this.adapter.getCustomerId();
      this.debug?.log('CustomerService', 'resolveCustomerId done', { hasId: customerId != null && customerId !== '' });
      this.bus.emitCrossTab<CustomerResolvedPayload>(TrackingBusEvent.CustomerResolved, { customerId }, { publisher: TrackingTag.CustomerService });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      this.debug?.log('CustomerService', 'resolveCustomerId failed', { message });
    }
  }
}
